import {
  makeStyles,
  tokens,
  Text,
  Card,
  CardHeader,
  Badge,
} from '@fluentui/react-components';
import type { TeamsQualityStream } from '../../types';

const useStyles = makeStyles({
  cardContent: {
    padding: tokens.spacingHorizontalM,
  },
  streamsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  streamSection: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  streamTitle: {
    marginBottom: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap' as const,
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontWeight: tokens.fontWeightSemibold,
  },
  deviceSection: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  noStreams: {
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
});

function formatDirection(dir: string | null): string {
  if (!dir) return 'Unknown';
  switch (dir) {
    case 'callerToCallee': return 'Caller → Callee';
    case 'calleeToCaller': return 'Callee → Caller';
    default: return dir;
  }
}

function formatRate(rate: number | null): string {
  if (rate === null) return '-';
  return `${(rate * 100).toFixed(2)}%`;
}

function formatMs(ms: number | null): string {
  if (ms === null) return '-';
  return `${ms.toFixed(1)}ms`;
}

interface TeamsQualityCardProps {
  qualityStreams: TeamsQualityStream[];
  className?: string;
}

export function TeamsQualityCard({ qualityStreams, className }: TeamsQualityCardProps) {
  const styles = useStyles();

  return (
    <Card className={className}>
      <CardHeader header={<Text weight="semibold">Teams Quality of Service</Text>} />
      <div className={styles.cardContent}>
        {qualityStreams.length === 0 ? (
          <div className={styles.noStreams}>
            <Text>No quality streams available yet (data may arrive with v2+ call record)</Text>
          </div>
        ) : (
          <div className={styles.streamsGrid}>
            {qualityStreams.map((stream, i) => (
              <div key={i} className={styles.streamSection}>
                <div className={styles.streamTitle}>
                  <Badge appearance="outline" size="small">
                    {formatDirection(stream.stream_direction)}
                  </Badge>
                  {stream.media_type && (
                    <Badge appearance="tint" size="small" style={{ textTransform: 'capitalize' }}>
                      {stream.media_type}
                    </Badge>
                  )}
                </div>

                {stream.caller_display_name && (
                  <div className={styles.metricRow}>
                    <Text className={styles.label}>Participants</Text>
                    <Text className={styles.value} size={200}>
                      {stream.caller_display_name} → {stream.callee_display_name || '?'}
                    </Text>
                  </div>
                )}

                <div className={styles.metricRow}>
                  <Text className={styles.label}>Avg Jitter</Text>
                  <Text className={styles.value}>{formatMs(stream.avg_jitter_ms)}</Text>
                </div>
                <div className={styles.metricRow}>
                  <Text className={styles.label}>Max Jitter</Text>
                  <Text className={styles.value}>{formatMs(stream.max_jitter_ms)}</Text>
                </div>
                <div className={styles.metricRow}>
                  <Text className={styles.label}>Avg Packet Loss</Text>
                  <Text className={styles.value}>{formatRate(stream.avg_packet_loss_rate)}</Text>
                </div>
                <div className={styles.metricRow}>
                  <Text className={styles.label}>Max Packet Loss</Text>
                  <Text className={styles.value}>{formatRate(stream.max_packet_loss_rate)}</Text>
                </div>
                <div className={styles.metricRow}>
                  <Text className={styles.label}>Avg RTT</Text>
                  <Text className={styles.value}>{formatMs(stream.avg_round_trip_time_ms)}</Text>
                </div>
                <div className={styles.metricRow}>
                  <Text className={styles.label}>Max RTT</Text>
                  <Text className={styles.value}>{formatMs(stream.max_round_trip_time_ms)}</Text>
                </div>

                {(stream.platform || stream.capture_device || stream.render_device) && (
                  <div className={styles.deviceSection}>
                    {stream.platform && (
                      <div className={styles.metricRow}>
                        <Text className={styles.label}>Platform</Text>
                        <Text className={styles.value}>{stream.platform}</Text>
                      </div>
                    )}
                    {stream.capture_device && (
                      <div className={styles.metricRow}>
                        <Text className={styles.label}>Microphone</Text>
                        <Text size={200}>{stream.capture_device}</Text>
                      </div>
                    )}
                    {stream.render_device && (
                      <div className={styles.metricRow}>
                        <Text className={styles.label}>Speaker</Text>
                        <Text size={200}>{stream.render_device}</Text>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
